import { useEffect, useMemo, useRef, useState } from 'react'
import { isReportsMockDataEnabled } from '@assets/mockdata/Reports/reportsByPage.mockdata'
import {
  useReportsErrorActions,
  useReportsRefreshKey,
} from '@context/ReportsRefreshContext'
import {
  useDashboardClinicBootstrapPending,
  useDashboardClinicId,
} from '@hooks/dashboard/useDashboardPeriod'
import type {
  ReportRequestOptions,
  ReportWithMeta,
} from '@services/api/reports/byPage.api'
import type {
  ReportBucketType,
  ReportQueryParams,
} from '@services/api/reports/finance.api'
import {
  buildReportQueryCacheKey,
  getReportQueryInflight,
  joinReportQueryInflight,
  leaveReportQueryInflight,
  readReportQueryCache,
  setReportQueryInflight,
  writeReportQueryCache,
} from '@utils/reportQueryCache.util'
import {
  formatBucketPeriodLabel,
  mapToolbarToFinanceQuery,
} from '@utils/reports/revenueRealizationFinance.util'

export type UseFinanceReportQueryResult<T> = {
  loading: boolean
  error: string | null
  data: (T & Partial<ReportWithMeta>) | null
  hasData: boolean | null
  bucketLabel: string
}

type UseFinanceReportQueryOptions<T> = {
  toolbarSelection: Record<string, string>
  fetchReport: (
    params: ReportQueryParams,
    options?: ReportRequestOptions,
  ) => Promise<T>
  getBucketFromData: (data: T) => ReportBucketType
  errorMessage: string
}

function isAbortError(error: unknown): boolean {
  const candidate = error as { code?: string; name?: string } | null
  return (
    candidate?.code === 'ERR_CANCELED' ||
    candidate?.name === 'CanceledError' ||
    candidate?.name === 'AbortError'
  )
}

export function useFinanceReportQuery<T>({
  toolbarSelection,
  fetchReport,
  getBucketFromData,
  errorMessage,
}: UseFinanceReportQueryOptions<T>): UseFinanceReportQueryResult<T> {
  const clinicId = useDashboardClinicId()
  const clinicBootstrapPending = useDashboardClinicBootstrapPending()
  const refreshKey = useReportsRefreshKey()
  const { setReportError } = useReportsErrorActions()
  const [data, setData] = useState<(T & Partial<ReportWithMeta>) | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bucketLabel, setBucketLabel] = useState('Monthly')
  const useMockData = isReportsMockDataEnabled()
  const effectiveClinicId = clinicId ?? (useMockData ? 'mock-clinic' : null)
  const previousRefreshKeyRef = useRef(refreshKey)

  const selectionKey = useMemo(
    () => JSON.stringify({ toolbarSelection, clinicId: effectiveClinicId }),
    [toolbarSelection, effectiveClinicId],
  )

  const queryParamsSignature = useMemo(
    () =>
      JSON.stringify(
        effectiveClinicId
          ? mapToolbarToFinanceQuery(toolbarSelection, effectiveClinicId)
          : null,
      ),
    // selectionKey is a semantic dependency for toolbar objects recreated by callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionKey, effectiveClinicId],
  )

  // Report-manager bootstrap can publish several filter/metric objects that all
  // resolve to the same API query. Preserve the params reference in that case so
  // the request effect does not restart for a semantically unchanged query.
  const queryParams = useMemo(
    (): ReportQueryParams | null => JSON.parse(queryParamsSignature),
    [queryParamsSignature],
  )

  const queryKey = useMemo(
    () =>
      queryParams
        ? buildReportQueryCacheKey(
            fetchReport,
            queryParams as unknown as Record<string, unknown>,
          )
        : null,
    [fetchReport, queryParams],
  )

  useEffect(() => {
    if (!queryParams || !queryKey) {
      if (clinicBootstrapPending && !useMockData) {
        setLoading(true)
        setError(null)
        setReportError(null)
      } else if (!clinicId) {
        setError('No clinic selected')
        setReportError('No clinic selected')
        setLoading(false)
      } else {
        setError(null)
        setReportError(null)
        setLoading(false)
      }
      return
    }

    let cancelled = false
    let activeRequest: Promise<T> | null = null
    const forceRefresh = previousRefreshKeyRef.current !== refreshKey
    previousRefreshKeyRef.current = refreshKey

    if (!forceRefresh) {
      const cached = readReportQueryCache<T>(queryKey)
      if (cached !== undefined) {
        setData(cached as T & Partial<ReportWithMeta>)
        setBucketLabel(formatBucketPeriodLabel(getBucketFromData(cached)))
        setLoading(false)
        setError(null)
        setReportError(null)
        return () => {
          cancelled = true
        }
      }
    }

    setLoading(true)
    setError(null)
    setReportError(null)

    const existing = forceRefresh
      ? undefined
      : getReportQueryInflight<T>(queryKey)
    if (existing && joinReportQueryInflight(queryKey)) {
      activeRequest = existing
    } else {
      const request = fetchReport(queryParams, {
        refresh: forceRefresh,
      }).then((result) => {
        writeReportQueryCache(queryKey, result)
        return result
      })
      activeRequest = setReportQueryInflight(queryKey, request)
    }

    void activeRequest
      .then((result) => {
        if (cancelled) return
        setData(result as T & Partial<ReportWithMeta>)
        setBucketLabel(formatBucketPeriodLabel(getBucketFromData(result)))
      })
      .catch((reason: unknown) => {
        if (cancelled || isAbortError(reason)) return
        const message = reason instanceof Error ? reason.message : errorMessage
        setError(message)
        setReportError(message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      // Keep the shared request alive so navigating between reports does not
      // restart the same expensive query. Its result still warms the cache.
      if (activeRequest) leaveReportQueryInflight(queryKey, activeRequest)
    }
  }, [
    queryKey,
    queryParams,
    clinicId,
    clinicBootstrapPending,
    useMockData,
    refreshKey,
    fetchReport,
    getBucketFromData,
    errorMessage,
    setReportError,
  ])

  const waitingForClinic =
    !useMockData && !queryParams && clinicBootstrapPending
  return {
    data,
    loading: loading || waitingForClinic,
    error: waitingForClinic ? null : error,
    hasData: data?.reportMeta?.hasData ?? null,
    bucketLabel,
  }
}
